/**
 * Registration API Service
 *
 * Handles all communication with the new backend's survey-based registration system.
 * These are public endpoints that require only an API key (no JWT auth).
 */

import apiClient from './client';
import type {
  SurveyApiResponse,
  SurveyDefinition,
  SubmitRegistrationSurveyRequest,
  RegistrationResponse,
  FileUploadResponse,
  FileUploadCallback,
  RegistrationProfileType,
} from '@/types/survey';

/**
 * Fetch survey definition for a given profile type.
 * The backend returns the survey definition with all questions.
 *
 * @param profileType - Profile type (e.g., 'provider', 'business_client', 'b2b', 'partnership')
 * @param providerType - Optional provider affiliation code. Only meaningful for provider /
 *   provider_nh; returns the survey tied to the matching onboarding strategy (specific → generic
 *   fallback). Ignored by the backend for non-provider profile types.
 * @returns SurveyDefinition with questions
 */
export async function getSurveyForProfileType(
  profileType: RegistrationProfileType,
  providerType?: string
): Promise<SurveyDefinition> {
  const code = providerType?.trim();
  const response = await apiClient.get<SurveyApiResponse>(
    `/api/surveys/registration/${profileType}/`,
    code ? { params: { provider_type: code } } : undefined
  );

  const data = response.data;

  // Check if no survey is configured
  if (data.no_survey_configured) {
    return {
      id: '',
      title: '',
      title_en: '',
      title_ar: '',
      description: '',
      questions: [],
      no_survey_configured: true,
      message: data.message,
    };
  }

  // Map API response to SurveyDefinition
  return {
    id: data.survey.id,
    snapshot_id: data.survey.snapshot_id ?? null,
    title: data.survey.title,
    title_en: data.survey.title_en,
    title_ar: data.survey.title_ar,
    description: data.survey.description,
    description_en: data.survey.description_en,
    description_ar: data.survey.description_ar,
    questions: data.questions || [],
  };
}

/**
 * Submit a registration survey with answers.
 *
 * @param data - Submission request containing survey_id, profile_type, and answers
 * @returns RegistrationResponse with user and profile data
 */
export async function submitRegistrationSurvey(
  data: SubmitRegistrationSurveyRequest
): Promise<RegistrationResponse> {
  try {
    const response = await apiClient.post<RegistrationResponse>(
      '/api/surveys/registration/submit/',
      data
    );
    return response.data;
  } catch (error: unknown) {
    // Extract meaningful error message from backend response
    if (
      error &&
      typeof error === 'object' &&
      'response' in error &&
      (error as { response?: { data?: { message?: string; error?: string } } }).response?.data
    ) {
      const responseData = (error as { response: { data: { message?: string; error?: string } } }).response.data;
      throw new Error(
        responseData.message || responseData.error || 'Registration submission failed'
      );
    }
    throw new Error('Registration submission failed. Please try again.');
  }
}

/**
 * Upload a file for a registration form question.
 *
 * @param file - File to upload
 * @param fileType - Type identifier (e.g., 'professional_license', 'graduation_certificate')
 * @param onProgress - Optional progress callback
 * @returns FileUploadResponse with file_id
 */
export async function uploadRegistrationFile(
  file: File,
  fileType: string,
  onProgress?: FileUploadCallback
): Promise<FileUploadResponse> {
  try {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('file_type', fileType);

    const response = await apiClient.post<FileUploadResponse>(
      '/api/documents/upload/',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            const percentage = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            onProgress({
              loaded: progressEvent.loaded,
              total: progressEvent.total,
              percentage,
            });
          }
        },
      }
    );

    return response.data;
  } catch (error: unknown) {
    if (
      error &&
      typeof error === 'object' &&
      'response' in error
    ) {
      const axiosError = error as { response?: { status?: number; data?: { message?: string } } };
      if (axiosError.response?.status === 400) {
        throw new Error(
          axiosError.response.data?.message ||
            'Invalid file. Please check the file type and size.'
        );
      } else if (axiosError.response?.status === 413) {
        throw new Error('File is too large. Please use a smaller file.');
      }
    }
    throw new Error('File upload failed. Please try again.');
  }
}

export const registrationApi = {
  getSurveyForProfileType,
  submitRegistrationSurvey,
  uploadRegistrationFile,
};
